import { syncGoogleAds, suspendGoogleAds, trackGoogleCheckout, trackVerifiedGooglePurchase } from "@/lib/google-ads";
import { validGoogleAdsUrlParameter } from "@/lib/google-ads-config";
import { matchComponentPagePath } from "@/lib/component-page-route";
import { matchResourceRoute } from "@/lib/resource-page-route";
import { matchPaidLandingRoute } from "@/lib/paid-landing-route";

// Public identifier, not the Marketing API / MCP access token.
export const META_PIXEL_ID = "1069491225915090";
export const CONSENT_KEY = "crimp:advertising-consent:v3";
export const OPEN_ADVERTISING_PREFERENCES = "crimp:open-advertising-preferences";
export function openAdvertisingPreferences(): void { window.dispatchEvent(new Event(OPEN_ADVERTISING_PREFERENCES)); }
const CONSENT_EVENT = "crimp:advertising-consent";
const CONSENT_LIFETIME = 180 * 24 * 60 * 60 * 1_000;
export type AdvertisingConsent = "granted" | "denied" | null;
let sessionChoice: AdvertisingConsent | undefined;
type AdvertisingRegion = "pending" | "eu" | "non-eu" | "unknown";
let advertisingRegion: AdvertisingRegion = "pending";
let regionRequest: Promise<void> | undefined;

export function readAdvertisingRegion(): AdvertisingRegion { return advertisingRegion; }

/** Resolve once per document; never persist an inferred region as consent. */
export function initializeAdvertisingRegion(): Promise<void> {
  return regionRequest ??= (async () => {
    try {
      const response = await fetch("/api/advertising-region", {
        cache: "no-store", credentials: "omit", referrerPolicy: "no-referrer",
        signal: AbortSignal.timeout(5_000),
      });
      if (!response.ok) throw new Error("Region lookup failed");
      const data = await response.json();
      advertisingRegion = data?.region === "eu" || data?.region === "non-eu" ? data.region : "unknown";
    } catch {
      advertisingRegion = "unknown";
    }
    syncMetaPixel();
    window.dispatchEvent(new Event(CONSENT_EVENT));
  })();
}

type Pixel = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Pixel;
  loaded: boolean;
  version: string;
};
type PixelWindow = Window & { fbq?: Pixel; _fbq?: Pixel };
let script: HTMLScriptElement | undefined;
let ready = false;
let pageViewSent = false;
let enteredEditor = false;
let qualifiedEditorSent = false;
let permanentlySuspended = false;
const sentPurchases = new Set<string>();
const MANUAL_SOURCES = new Set(["palette_click", "mobile_palette", "palette_drag", "custom_component", "paste", "replace", "customize", "revert"]);

export function advertisingAllowed(): boolean {
  if (advertisingRegion === "pending" || advertisingRegion === "unknown" || navigator.doNotTrack === "1" ||
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return false;
  const choice = readAdvertisingConsent();
  return choice === "granted" || (advertisingRegion === "non-eu" && choice !== "denied");
}

/** Revoke BEFORE a private URL reaches history or SDK navigation listeners.
 * Once this happens, this document never resumes advertising capture. */
export function guardMetaNavigation(): void {
  if (!publicUrl(new URL(window.location.href))) suspend();
  for (const method of ["pushState", "replaceState"] as const) {
    const original = window.history[method].bind(window.history);
    window.history[method] = (data, unused, url) => {
      if (url != null && !publicUrl(new URL(String(url), window.location.href))) suspend();
      original(data, unused, url);
    };
  }
  window.addEventListener("popstate", () => { if (!canTrack()) suspend(); });
  window.addEventListener("hashchange", () => { if (!canTrack()) suspend(); });
}
function suspend(): void {
  suspendGoogleAds();
  permanentlySuspended = true;
  const pixel = (window as PixelWindow).fbq;
  if (pixel) {
    pixel.queue = pixel.queue.filter((args) => !String(args[0]).startsWith("track"));
    pixel("consent", "revoke");
  }
}

/** Explicit allowlist: none of the product analytics properties leave Crimp. */
export function trackMetaProductEvent(event: string, props?: Record<string, unknown>): void {
  if (event === "app_entered") enteredEditor = true;
  if (event !== "equipment_count_changed" || !enteredEditor || qualifiedEditorSent ||
    window.location.pathname !== "/app" || props?.successful !== true ||
    !["add", "paste", "replace"].includes(String(props.operation)) ||
    !MANUAL_SOURCES.has(String(props.mutation_source))) return;
  if (dispatch("QualifiedEditor", { definition_version: "manual-editor-v1" }, undefined, true)) qualifiedEditorSent = true;
}

export function trackMetaCheckout(): void {
  syncMetaPixel();
  try { trackGoogleCheckout(); } catch { /* Optional measurement must not interrupt checkout. */ }
  dispatch("InitiateCheckout", { content_name: "Crimp", content_type: "product", num_items: 1 });
}

export type MetaPurchaseReceipt = { eventId: string; value: number; currency: string };
/** Called only with the authenticated server's transaction-specific receipt. */
export function trackVerifiedMetaPurchase(receipt: MetaPurchaseReceipt): boolean {
  if (!/^crimp_purchase_[a-f0-9]{64}$/.test(receipt.eventId) ||
    !Number.isFinite(receipt.value) || receipt.value <= 0 || !/^[A-Z]{3}$/.test(receipt.currency)) return false;
  const key = `crimp:meta-purchase:${receipt.eventId}`;
  if (sentPurchases.has(receipt.eventId)) return true;
  try { const sentAt = Number(localStorage.getItem(key)); if (sentAt > 0 && Date.now() - sentAt < 172_800_000) return true; } catch { /* Memory dedup still applies. */ }
  if (!dispatch("Purchase", { value: receipt.value, currency: receipt.currency, content_name: "Crimp", num_items: 1 }, receipt.eventId)) return false;
  sentPurchases.add(receipt.eventId);
  try { localStorage.setItem(key, String(Date.now())); } catch { /* Meta also receives the stable event ID. */ }
  return true;
}
/** Each destination retries independently; a blocked SDK must not hide the other. */
export function trackVerifiedAdvertisingPurchase(receipt: MetaPurchaseReceipt): boolean {
  syncMetaPixel();
  let meta = false;
  let google = false;
  try { meta = trackVerifiedMetaPurchase(receipt); } catch { /* Retry independently. */ }
  try { google = trackVerifiedGooglePurchase(receipt); } catch { /* Retry independently. */ }
  return meta && google;
}
function dispatch(event: string, params: Record<string, unknown>, eventId?: string, custom = false): boolean {
  if (!advertisingAllowed() || !canTrack()) return false;
  syncMetaPixel();
  if (!ready) return false; // No replay of actions before consent / SDK readiness.
  (window as PixelWindow).fbq?.(custom ? "trackSingleCustom" : "trackSingle", META_PIXEL_ID, event, params, ...(eventId ? [{ eventID: eventId }] : []));
  return true;
}

export function readAdvertisingConsent(): AdvertisingConsent {
  if (sessionChoice !== undefined) return sessionChoice;
  try {
    const stored = JSON.parse(localStorage.getItem(CONSENT_KEY) ?? "null");
    // Earlier acceptances named Meta only; Google needs a new decision.
    // Preserve unexpired refusals from either earlier scope.
    if (!stored) {
      for (const key of ["crimp:meta-advertising-consent:v2", "crimp:meta-advertising-consent:v1"]) {
        const previous = JSON.parse(localStorage.getItem(key) ?? "null");
        if (previous?.choice === "denied" && typeof previous.savedAt === "number" &&
          previous.savedAt <= Date.now() && Date.now() - previous.savedAt < CONSENT_LIFETIME) return "denied";
      }
    }
    if (
      (stored?.choice === "granted" || stored?.choice === "denied") &&
      typeof stored.savedAt === "number" &&
      stored.savedAt <= Date.now() &&
      Date.now() - stored.savedAt < CONSENT_LIFETIME
    ) return stored.choice;
  } catch { /* Missing or blocked storage means no consent. */ }
  return null;
}

export function saveAdvertisingConsent(choice: "granted" | "denied"): void {
  // Honor this visit's choice even if the browser disallows persistence.
  sessionChoice = choice;
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, savedAt: Date.now() }));
    sessionChoice = undefined;
  } catch { /* The choice lasts only for this document. */ }
  syncMetaPixel();
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function subscribeAdvertisingConsent(notify: () => void): () => void {
  const refresh = () => {
    syncMetaPixel();
    notify();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== CONSENT_KEY && event.key !== null) return;
    sessionChoice = undefined;
    refresh();
  };
  window.addEventListener(CONSENT_EVENT, notify);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", refresh);
  window.addEventListener("pageshow", refresh);
  return () => {
    window.removeEventListener(CONSENT_EVENT, notify);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", refresh);
    window.removeEventListener("pageshow", refresh);
  };
}

function validAdvertisingUrlParameter(key: string, value: string): boolean {
  return validGoogleAdsUrlParameter(key, value) ||
    (["fbclid", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"].includes(key) && /^[a-zA-Z0-9_.~-]{1,500}$/.test(value));
}

function publicUrl(url: URL): boolean {
  // Private links, auth callbacks, draft fragments and arbitrary parameters
  // must never reach Meta in its automatic page-location/referrer fields.
  if (url.hash) return false;
  if (url.pathname === "/app") {
    return [...url.searchParams].every(([key, value]) =>
      (key === "new" && value === "1") ||
      validAdvertisingUrlParameter(key, value));
  }
  if ([...url.searchParams].some(([key, value]) => !validAdvertisingUrlParameter(key, value))) return false;
  const path = url.pathname.replace(/\/$/, "") || "/";
  return ["/", "/landing", "/pricing", "/checkout/success", "/terms", "/privacy", "/refunds", "/components", "/methodology", "/developers"].includes(path) ||
    matchPaidLandingRoute(url.pathname) !== null || matchResourceRoute(path) !== null || matchComponentPagePath(path) !== null;
}

function canTrack(): boolean {
  if (permanentlySuspended || window.location.origin !== "https://crimp.studio" || !publicUrl(new URL(window.location.href))) return false;
  // Same-origin referrers can contain a private project/share URL too.
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin === window.location.origin ? !publicUrl(referrer) : Boolean(referrer.search || referrer.hash)) return false;
    } catch { return false; }
  }
  return true;
}

function clearAdvertisingCookies(): void {
  // Meta's first-party cookies may be host-only or on a parent domain.
  for (const name of ["_fbp", "_fbc"]) {
    for (const domain of ["", window.location.hostname, ".crimp.studio"]) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
    }
  }
}

export function syncMetaPixel(): void {
  syncGoogleAds(advertisingAllowed() && canTrack(), !advertisingAllowed() && advertisingRegion !== "pending");
  const pixelWindow = window as PixelWindow;
  if (!advertisingAllowed() || !canTrack()) {
    // https://developers.facebook.com/documentation/meta-pixel/implementation/gdpr
    pixelWindow.fbq?.("consent", "revoke");
    // A location lookup happens on every document. Do not erase a permitted
    // visitor's attribution cookies while that lookup is still in flight.
    if (!advertisingAllowed() && advertisingRegion !== "pending") clearAdvertisingCookies();
    return;
  }

  if (!script) {
    // Load only after the regional policy and visitor preferences allow it.
    const pixel = function (...args: unknown[]) {
      if (pixel.callMethod) pixel.callMethod(...args);
      else pixel.queue.push(args);
    } as Pixel;
    pixel.queue = [];
    pixel.push = pixel;
    pixel.loaded = true;
    pixel.version = "2.0";
    pixelWindow.fbq = pixel;
    pixelWindow._fbq = pixel;
    pixel("consent", "revoke");
    // Manual-only mode: no automatic button/form metadata or matching data.
    // https://developers.facebook.com/documentation/meta-pixel/advanced#automatic-configuration
    pixel("set", "autoConfig", false, META_PIXEL_ID);
    pixel("init", META_PIXEL_ID);
    script = document.createElement("script");
    script.id = "crimp-meta-pixel";
    script.async = true;
    script.referrerPolicy = "no-referrer";
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.onload = () => {
      ready = true;
      // Recheck consent: the visitor may have declined while loading.
      syncMetaPixel();
    };
    document.head.appendChild(script);
  }
  if (ready) {
    pixelWindow.fbq?.("consent", "grant");
    if (!pageViewSent && window.location.pathname !== "/app" && window.location.pathname !== "/checkout/success") {
      pixelWindow.fbq?.("trackSingle", META_PIXEL_ID, "PageView");
      pageViewSent = true;
    }
  }
}
